/**
 * CAIP-2 (Chain Agnostic Improvement Proposal 2) utilities for blockchain chain identification.
 *
 * @remarks
 * This module provides utilities for normalizing and parsing blockchain chain identifiers
 * according to the CAIP-2 standard. Supports multiple namespaces including EVM (eip155),
 * Solana chains, and Bitcoin (bip122).
 *
 * @module CAIP-2
 * @category Internal
 * @packageDocumentation
 */
import { ChainInvalidChainIdError, ChainInvalidNamespaceError } from "./error";
import { hexToDecimalString, isDecimalString, isHexString } from "./utils";
/**
 * Solana genesis hash mappings for known clusters, used to set up the caip2 namespaces.
 * https://github.com/ChainAgnostic/namespaces/blob/main/solana/caip2.md
 */
const SOLANA_GENESIS_HASHES = {
    mainnet: "5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
    testnet: "4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
};
/**
 * Bitcoin genesis hash mappings for known networks, used to set up the caip2 namespaces.
 * https://github.com/ChainAgnostic/namespaces/blob/main/bip122/caip2.md
 */
const BITCOIN_GENESIS_HASHES = {
    mainnet: "000000000019d6689c085ae165831e93",
    testnet: "000000000933ea01ad0ee984209779baaec3ced90fa3f408719526f8d77f4943",
};
const SUPPORTED_CAIP2_NAMESPACES = ["eip155", "solana", "bip122"];
/** Helper to get supported Solana aliases for error messages */
const getSupportedSolanaAliases = () => Object.keys(SOLANA_GENESIS_HASHES).join(", ");
/** Helper to get supported Solana genesis hashes for error messages */
const getSupportedSolanaHashes = () => Object.values(SOLANA_GENESIS_HASHES).join(", ");
/** Helper to get supported Bitcoin aliases for error messages */
const getSupportedBitcoinAliases = () => Object.keys(BITCOIN_GENESIS_HASHES).join(", ");
/** Helper to get supported Bitcoin genesis hashes for error messages */
const getSupportedBitcoinHashes = () => Object.values(BITCOIN_GENESIS_HASHES).join(", ");
/** Type guard to check if a string is in strict CAIP-2 format. */
const isCAIP2 = (s) => /^[a-z0-9]{3,8}:[A-Za-z0-9._-]+$/.test(s);
/** Parse a CAIP-2 identifier into its components. */
function parseCAIP2(identifier) {
    const parts = identifier.split(":");
    if (parts.length !== 2) {
        throw new Error("Invalid CAIP-2 identifier format");
    }
    const [namespace, reference] = parts;
    if (!namespace || !reference) {
        throw new Error("Namespace or reference cannot be empty");
    }
    return { namespace, reference };
}
/**
 * Ensure a provided chain ID is not empty.
 *
 * Throws an error when the chain ID is undefined, null, or only whitespace.
 */
function ensureNonEmptyChainId(chainID) {
    if (chainID === undefined || chainID === null) {
        throw new Error("Chain ID must be provided");
    }
    const trimmed = chainID.trim();
    if (trimmed === "") {
        throw new Error("Chain ID must be provided");
    }
    return trimmed;
}
/**
 * Detects the blockchain namespace from a chain ID string.
 *
 * @remarks
 * Extracts the namespace prefix from a CAIP-2 formatted chain identifier.
 * Only accepts strings containing a colon separator (`namespace:reference`).
 * Bare numeric strings (`"1"`), hex strings (`"0x1"`), or alias names (`"mainnet"`)
 * are **not** supported and will throw.
 *
 * @param chainID - Chain identifier in CAIP-2 format (namespace:reference)
 * @returns The detected namespace ("eip155", "solana", "bip122", etc.)
 * @throws {ChainInvalidNamespaceError} If the string has no colon or the namespace is unsupported
 *
 * @example
 * ```typescript
 * detectNamespace("eip155:1")     // "eip155"
 * detectNamespace("solana:mainnet") // "solana"
 * detectNamespace("bip122:000000000019d6689c085ae165831e93") // "bip122"
 * ```
 *
 * @public
 */
export function detectNamespace(chainID) {
    // 1. CAIP-2 format: extract namespace before ":"
    if (chainID.includes(":")) {
        const namespace = chainID.split(":")[0];
        if (!namespace) {
            throw new ChainInvalidNamespaceError(chainID);
        }
        // Validate it's a supported namespace
        if (!SUPPORTED_CAIP2_NAMESPACES.includes(namespace)) {
            throw new ChainInvalidNamespaceError(chainID);
        }
        return namespace;
    }
    throw new ChainInvalidNamespaceError(chainID);
}
function isSolanaSupportedChain(suffix) {
    return (Object.keys(SOLANA_GENESIS_HASHES).includes(suffix) ||
        Object.values(SOLANA_GENESIS_HASHES).includes(suffix));
}
function isBitcoinSupportedChain(suffix) {
    return (Object.keys(BITCOIN_GENESIS_HASHES).includes(suffix) ||
        Object.values(BITCOIN_GENESIS_HASHES).includes(suffix));
}
/**
 * Normalize Solana chain identifier to genesis hash format.
 * Accepts both genesis hash and human-readable alias as input.
 * @param suffix - The chain suffix (e.g., "mainnet" or "5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp")
 * @returns The genesis hash (e.g., "5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp")
 */
function normalizeSolanaChainToGenesisHash(suffix) {
    if (Object.values(SOLANA_GENESIS_HASHES).includes(suffix)) {
        return suffix;
    }
    if (Object.prototype.hasOwnProperty.call(SOLANA_GENESIS_HASHES, suffix)) {
        return SOLANA_GENESIS_HASHES[suffix];
    }
    throw new ChainInvalidChainIdError(`solana:${suffix}`, "solana", `Supported aliases: [${getSupportedSolanaAliases()}]. Supported genesis hashes: [${getSupportedSolanaHashes()}].`);
}
/**
 * Normalize Bitcoin chain identifier to genesis hash format.
 * Accepts both genesis hash and human-readable alias as input.
 * @param suffix - The chain suffix (e.g., "mainnet" or "000000000019d6689c085ae165831e93")
 * @returns The genesis hash (e.g., "000000000019d6689c085ae165831e93")
 */
function normalizeBitcoinChainToGenesisHash(suffix) {
    if (Object.values(BITCOIN_GENESIS_HASHES).includes(suffix)) {
        return suffix;
    }
    if (Object.prototype.hasOwnProperty.call(BITCOIN_GENESIS_HASHES, suffix)) {
        return BITCOIN_GENESIS_HASHES[suffix];
    }
    throw new ChainInvalidChainIdError(`bip122:${suffix}`, "bip122", `Supported aliases: [${getSupportedBitcoinAliases()}]. Supported genesis hashes: [${getSupportedBitcoinHashes()}].`);
}
/** Normalize a chain ID to CAIP-2 standard format with genesis hash references. */
export function normalizeChainID(chainID, namespace = "eip155") {
    const raw = ensureNonEmptyChainId(chainID).trim();
    const suffix = raw.split(":")[1];
    // Handle Solana: always normalize to genesis hash format
    // e.g., "solana:mainnet" -> "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp"
    if (namespace === "solana" && suffix && isSolanaSupportedChain(suffix)) {
        return `solana:${normalizeSolanaChainToGenesisHash(suffix)}`;
    }
    // Handle Solana aliases or hashes without namespace prefix
    if (namespace === "solana" && isSolanaSupportedChain(raw)) {
        return `solana:${normalizeSolanaChainToGenesisHash(raw)}`;
    }
    // Handle Bitcoin: normalize known aliases to genesis hash format
    // e.g., "bip122:mainnet" -> "bip122:000000000019d6689c085ae165831e93"
    if (namespace === "bip122" && suffix && isBitcoinSupportedChain(suffix)) {
        return `bip122:${normalizeBitcoinChainToGenesisHash(suffix)}`;
    }
    // Handle Bitcoin aliases or hashes without namespace prefix
    if (namespace === "bip122" && isBitcoinSupportedChain(raw)) {
        return `bip122:${normalizeBitcoinChainToGenesisHash(raw)}`;
    }
    // If already in CAIP-2 format with solana namespace, normalize to genesis hash
    if (isCAIP2(raw) && raw.startsWith("solana:")) {
        const solanaRef = raw.split(":")[1];
        if (solanaRef && isSolanaSupportedChain(solanaRef)) {
            return `solana:${normalizeSolanaChainToGenesisHash(solanaRef)}`;
        }
        throw new ChainInvalidChainIdError(raw, "solana", `Supported aliases: [${getSupportedSolanaAliases()}]. Supported genesis hashes: [${getSupportedSolanaHashes()}].`);
    }
    // If already in CAIP-2 format with bitcoin namespace, normalize to genesis hash if known
    if (isCAIP2(raw) && raw.startsWith("bip122:")) {
        const bitcoinRef = raw.split(":")[1];
        if (bitcoinRef && isBitcoinSupportedChain(bitcoinRef)) {
            return `bip122:${normalizeBitcoinChainToGenesisHash(bitcoinRef)}`;
        }
    }
    // If already in CAIP-2 format (non-Solana), return as-is
    if (isCAIP2(raw)) {
        return raw;
    }
    // Handle non-numeric references for non-EVM chains
    // For chains like Solana, Stellar, etc., the reference might be a string identifier
    if (namespace !== "eip155") {
        // Accept alphanumeric references for non-EVM chains
        if (/^[A-Za-z0-9._-]+$/.test(raw)) {
            const result = `${namespace}:${raw}`;
            if (isCAIP2(result)) {
                return result;
            }
        }
        throw new ChainInvalidChainIdError(chainID, namespace, "Invalid chain ID format");
    }
    // Handle numeric chain IDs (hex or decimal) for EVM chains
    let normalizedChainID;
    if (/^0x[0-9a-f]+$/i.test(raw)) {
        // Hex format (e.g., "0x1")
        normalizedChainID = Number.parseInt(raw, 16).toString();
    }
    else if (/^[0-9]+$/.test(raw)) {
        // Decimal format (e.g., "1")
        normalizedChainID = raw;
    }
    else {
        // Invalid format for EVM chains
        throw new ChainInvalidChainIdError(chainID, namespace, "EVM chains require numeric chain IDs (decimal or hex format)");
    }
    const result = `${namespace}:${normalizedChainID}`;
    if (!isCAIP2(result)) {
        throw new ChainInvalidChainIdError(chainID, namespace, "Invalid CAIP-2 format");
    }
    return result;
}
/** Get the EVM numeric chain ID from a CAIP-2 or flexible input. */
export function getEvmChainIdAsInt(identifier) {
    if (!identifier || identifier.trim() === "") {
        throw new ChainInvalidChainIdError("", "eip155", "Chain identifier cannot be empty");
    }
    const caip2 = normalizeChainID(identifier, "eip155");
    const { namespace, reference } = parseCAIP2(caip2);
    if (namespace !== "eip155") {
        throw new ChainInvalidNamespaceError(caip2);
    }
    return Number.parseInt(reference, 10);
}
/**
 * Converts various chain ID formats into a numeric string (e.g. "1" for Ethereum mainnet).
 * Extracts the numeric reference from CAIP-2 format or converts hex/decimal strings.
 *
 * This function handles basic numeric conversions only. For non-EVM chain mappings
 * (e.g., Solana, Bitcoin to expand.network IDs), use `getExpandNetworkChainId` from
 * the chain service.
 *
 * @param identifier - Chain ID input (numeric string, hex, or CAIP-2 format with numeric reference)
 * @returns The numeric chain ID string, or undefined if not convertible
 *
 * @example
 * ```typescript
 * toNumericChainIDString("eip155:1")  // "1"
 * toNumericChainIDString("1")         // "1"
 * toNumericChainIDString("0x1")       // "1"
 * toNumericChainIDString("cosmos:1")  // "1"
 * toNumericChainIDString("solana:mainnet")  // undefined (non-numeric reference)
 * ```
 */
export function toNumericChainIDString(identifier) {
    if (identifier === undefined)
        return undefined;
    const trimmed = identifier.trim();
    // Handle CAIP-2 format (namespace:reference)
    if (trimmed.includes(":")) {
        const parts = trimmed.split(":");
        if (parts.length === 2) {
            const [namespace, reference] = parts;
            if (!namespace || !reference) {
                return undefined;
            }
            // If the reference is numeric, return it
            if (isDecimalString(reference)) {
                return reference;
            }
            // Non-numeric reference
            return undefined;
        }
    }
    // Handle hex format (e.g., "0x1")
    if (isHexString(trimmed)) {
        return hexToDecimalString(trimmed);
    }
    // Handle decimal format (e.g., "1", "137")
    if (isDecimalString(trimmed)) {
        return trimmed;
    }
    return undefined;
}
/**
 * Returns the CAIP-2 reference portion from a chain identifier.
 * If the input is already a reference (e.g., "1"), it is returned unchanged.
 * Solana and Bitcoin aliases are normalized to their genesis hash references.
 *
 * @example
 * ```typescript
 * toCAIP2Reference("1")  // "1"
 * toCAIP2Reference("eip155:1")  // "1"
 * toCAIP2Reference("solana:mainnet")  // "5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp"
 * toCAIP2Reference("solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp")  // "5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp"
 * toCAIP2Reference("bip122:mainnet")  // "000000000019d6689c085ae165831e93"
 * ```
 */
export function toCAIP2Reference(identifier) {
    const trimmed = identifier.trim();
    const namespace = trimmed.startsWith("solana:")
        ? "solana"
        : trimmed.startsWith("bip122:")
            ? "bip122"
            : "eip155";
    const caip2 = normalizeChainID(identifier, namespace);
    return parseCAIP2(caip2).reference;
}
/**
 * Normalize a single chainId/chainID string to CAIP-2 format.
 * - For EVM chains: numeric input -> "eip155:<number>"
 * - For Solana chains: normalize aliases to genesis hash
 * - For Bitcoin chains: normalize aliases to genesis hash
 *
 * @example
 * ```typescript
 * normalizeChainIdString("1")  // "eip155:1"
 * normalizeChainIdString("solana:mainnet")  // "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp"
 * normalizeChainIdString("solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp")  // "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp"
 * normalizeChainIdString("bip122:mainnet")  // "bip122:000000000019d6689c085ae165831e93"
 * ```
 */
export function normalizeChainIdString(value) {
    if (!value)
        return "";
    const trimmed = value.trim();
    if (!trimmed)
        return "";
    // Handle Solana chains: normalize to genesis hash format
    if (trimmed.startsWith("solana:")) {
        const suffix = trimmed.split(":")[1];
        if (suffix && isSolanaSupportedChain(suffix)) {
            return `solana:${normalizeSolanaChainToGenesisHash(suffix)}`;
        }
        return trimmed; // Return as-is if not a recognized Solana chain
    }
    // Handle Bitcoin chains: normalize to genesis hash format
    if (trimmed.startsWith("bip122:")) {
        const suffix = trimmed.split(":")[1];
        if (suffix && isBitcoinSupportedChain(suffix)) {
            return `bip122:${normalizeBitcoinChainToGenesisHash(suffix)}`;
        }
        return trimmed; // Return as-is if not a recognized Bitcoin chain
    }
    // Keep CAIP-2 EVM identifiers as-is
    if (trimmed.startsWith("eip155:")) {
        return trimmed;
    }
    // Handle EVM chains: wrap numeric input in CAIP-2 format
    return isDecimalString(trimmed) ? `eip155:${trimmed}` : trimmed;
}
/**
 * Deeply normalize all chainId/chainID properties in an object/array graph
 * to numeric string form. Returns a cloned structure.
 */
export function normalizeChainIdsDeep(input) {
    if (Array.isArray(input)) {
        return input.map((v) => normalizeChainIdsDeep(v));
    }
    if (input !== null && typeof input === "object") {
        const out = {};
        for (const [k, v] of Object.entries(input)) {
            if ((k === "chainId" || k === "chainID") && typeof v === "string") {
                out[k] = normalizeChainIdString(v);
            }
            else {
                out[k] = normalizeChainIdsDeep(v);
            }
        }
        return out;
    }
    return input;
}
/**
 * Normalize an optional chainId to CAIP-2 format or return undefined if not provided.
 * Reuses normalizeChainID for all supported formats (CAIP-2, decimal, hex, and aliases).
 */
export function normalizeOptionalChainIdToCAIP2(chainId, namespace = "eip155") {
    if (chainId === undefined)
        return undefined;
    return normalizeChainID(chainId, namespace);
}
//# sourceMappingURL=data:application/json;base64,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